import { lazy, Suspense, type ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { PageLoader } from "./components/ui";
import { SessionProvider, useSession } from "./session";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import CreateOrg from "./pages/CreateOrg";
import AccountPage from "./pages/AccountPage";
import OrgChoice from "./pages/OrgChoice";
import NotFound from "./pages/NotFound";
import HomePage from "./library/HomePage";
import LibraryPage from "./library/LibraryPage";
import UploadPage from "./upload/UploadPage";
import NotificationsPage from "./notifications/NotificationsPage";
import TeamsPage from "./library/TeamsPage";
import FolderPage from "./library/FolderPage";
import TrashPage from "./library/TrashPage";
import SearchPage from "./library/SearchPage";
import WatchPage from "./watch/WatchPage";
import SharePage from "./watch/SharePage";
import EmbedPage from "./watch/EmbedPage";
import EmbedGracklePage from "./watch/EmbedGracklePage";
import RecorderPage from "./recorder/RecorderPage";
import ProcessingPage from "./recorder/ProcessingPage";
import HudPage from "./hud/HudPage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import MeetingDetectedBanner from "./components/MeetingDetectedBanner";
import DesktopAuthReturn from "./components/DesktopAuthReturn";
import DesktopUpdateNotice from "./components/DesktopUpdateNotice";
import TraySync from "./components/TraySync";
import DesktopAuthPage from "./pages/DesktopAuthPage";
import DevelopersPage from "./pages/DevelopersPage";
import RecoveryProvider from "./recorder/recovery/RecoveryProvider";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import JoinLinkAcceptPage from "./pages/JoinLinkAcceptPage";
import ImpersonateConsumePage from "./pages/ImpersonateConsumePage";
import AdminLayout from "./admin/AdminLayout";
import OverviewPage from "./admin/OverviewPage";
import MembersPage from "./admin/MembersPage";
import ContentPage from "./admin/ContentPage";
import RetentionPage from "./admin/RetentionPage";
import LegalHoldsPage from "./admin/LegalHoldsPage";
import OrgSettingsPage from "./admin/OrgSettingsPage";
import AuditLogPage from "./admin/AuditLogPage";
import SsoSettingsPage from "./admin/SsoSettingsPage";
import AiSettingsPage from "./admin/AiSettingsPage";
import IntegrationsPage from "./admin/IntegrationsPage";
import WebhooksPage from "./admin/WebhooksPage";
import OpsLayout from "./ops/OpsLayout";
import OpsOverviewPage from "./ops/OpsOverviewPage";
import OpsOrgsPage from "./ops/OpsOrgsPage";
import OpsOrgDetailPage from "./ops/OpsOrgDetailPage";
import OpsJobsPage from "./ops/OpsJobsPage";
import OpsVideosPage from "./ops/OpsVideosPage";
import OpsMailPage from "./ops/OpsMailPage";
import OpsIntegrationsPage from "./ops/OpsIntegrationsPage";
import OpsNotetakerPage from "./ops/OpsNotetakerPage";
import OpsMediaPage from "./ops/OpsMediaPage";
import OpsTokensPage from "./ops/OpsTokensPage";
import LiveMeetingPreview from "./pages/LiveMeetingPreview";
import LiveNotesPage from "./pages/LiveNotesPage";
import MeetingsPage from "./library/MeetingsPage";
import NotesPage from "./notes/NotesPage";
import AskPage from "./ask/AskPage";
import ExtensionBridge from "./pages/ExtensionBridge";

// SD35: the @grackle/ui gallery exists only in dev builds. `import.meta.env.DEV` is a build-time
// constant, so the production bundle drops the import and the route entirely.
const DevRoutes = import.meta.env.DEV ? lazy(() => import("./dev/DevRoutes")) : null;

/** Gate a route on an authenticated session, optionally requiring a verified email (§4's
 * "E" auth column). Kept here rather than as its own file — it's routing glue, not a page. */
function RequireAuth({
  children,
  requireVerified,
}: {
  children: ReactElement;
  requireVerified?: boolean;
}) {
  const { user, loading } = useSession();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/signin" replace />;
  if (requireVerified && !user.email_verified) return <Navigate to="/verify" replace />;
  return children;
}

/** "/" resolves once the initial session check lands, so a returning signed-in visitor
 * skips the sign-in screen. */
function RootRedirect() {
  const { user, loading } = useSession();
  if (loading) return <PageLoader />;
  return <Navigate to={user ? "/orgs" : "/signin"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <ImpersonationBanner />
        <DesktopAuthReturn />
        <DesktopUpdateNotice />
        <TraySync />
        <MeetingDetectedBanner />
        <RecoveryProvider />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          {/* PR4: no AppShell, no auth gate — the HUD window (clients/desktop/src/hud.rs) loads
              this directly, and it has to render something sane in a plain browser too. */}
          <Route path="/hud" element={<HudPage />} />
          <Route path="/signin" element={<SignIn />} />
          {/* F18: public API reference, rendered from /api/v1/openapi.json; no auth gate. */}
          <Route path="/developers" element={<DevelopersPage />} />
          <Route path="/desktop-auth" element={<DesktopAuthPage />} />
          <Route path="/extension-bridge" element={<ExtensionBridge />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/invite" element={<InviteAcceptPage />} />
          <Route path="/join" element={<JoinLinkAcceptPage />} />
          <Route path="/auth/impersonate" element={<ImpersonateConsumePage />} />
          <Route
            path="/orgs/new"
            element={
              <RequireAuth requireVerified>
                <CreateOrg />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs"
            element={
              <RequireAuth>
                <OrgChoice />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountPage />
              </RequireAuth>
            }
          />
          {/* Reset PR2: /o/:slug is Home; the library moved to /o/:slug/library. HomePage
              redirects old library deep links (?q= / ?filter=) there. */}
          <Route
            path="/o/:slug"
            element={
              <RequireAuth>
                <HomePage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/upload"
            element={
              <RequireAuth requireVerified>
                <UploadPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/library"
            element={
              <RequireAuth>
                <LibraryPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/notifications"
            element={
              <RequireAuth>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/teams"
            element={
              <RequireAuth>
                <TeamsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/trash"
            element={
              <RequireAuth>
                <TrashPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/folders/:fid"
            element={
              <RequireAuth>
                <FolderPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/search"
            element={
              <RequireAuth>
                <SearchPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/meetings"
            element={
              <RequireAuth requireVerified>
                <MeetingsPage />
              </RequireAuth>
            }
          />
          {/* F11: Ask across meetings. */}
          <Route
            path="/o/:slug/ask"
            element={
              <RequireAuth>
                <AskPage />
              </RequireAuth>
            }
          />
          {/* Reset PR3: meeting notes scaffold (FR-M6, filled by F1). */}
          <Route
            path="/o/:slug/meetings/:grackleId/notes"
            element={
              <RequireAuth>
                <NotesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/live-meeting"
            element={
              <RequireAuth>
                <LiveNotesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/live-meeting/test"
            element={
              <RequireAuth requireVerified>
                <LiveMeetingPreview />
              </RequireAuth>
            }
          />
          <Route
            path="/o/:slug/admin"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="retention" element={<RetentionPage />} />
            <Route path="legal-holds" element={<LegalHoldsPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="sso" element={<SsoSettingsPage />} />
            <Route path="ai" element={<AiSettingsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="settings" element={<OrgSettingsPage />} />
          </Route>
          <Route
            path="/record"
            element={
              <RequireAuth requireVerified>
                <RecorderPage />
              </RequireAuth>
            }
          />
          <Route
            path="/record/:grackleId/processing"
            element={
              <RequireAuth requireVerified>
                <ProcessingPage />
              </RequireAuth>
            }
          />
          <Route path="/g/:grackleId" element={<WatchPage />} />
          <Route path="/s/:token" element={<SharePage />} />
          {/* F3/§2.13: the server's own /embed/{token} route (link_preview.rs) serves this SPA's
              built index.html — no app shell, no topbar, so unfurl consumers (Cipher, Slack,
              Notion, Linear, Jira) get a bare player in their iframe. */}
          <Route path="/embed/:token" element={<EmbedPage />} />
          <Route path="/embed/g/:grackleId" element={<EmbedGracklePage />} />
          <Route
            path="/ops"
            element={
              <RequireAuth>
                <OpsLayout />
              </RequireAuth>
            }
          >
            <Route index element={<OpsOverviewPage />} />
            <Route path="orgs" element={<OpsOrgsPage />} />
            <Route path="orgs/:orgId" element={<OpsOrgDetailPage />} />
            <Route path="videos" element={<OpsVideosPage />} />
            <Route path="videos/:grackleId" element={<OpsVideosPage />} />
            <Route path="jobs" element={<OpsJobsPage />} />
            <Route path="email" element={<OpsMailPage />} />
            <Route path="integrations" element={<OpsIntegrationsPage />} />
            <Route path="notetaker" element={<OpsNotetakerPage />} />
            <Route path="media" element={<OpsMediaPage />} />
            <Route path="tokens" element={<OpsTokensPage />} />
          </Route>
          {DevRoutes && (
            <Route
              path="/dev/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DevRoutes />
                </Suspense>
              }
            />
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
